const DEBUG_CONSOLE_STORAGE_KEY = 'fingertip_debug_console_enabled'
const MAX_ALL_LOG_ENTRIES = 500
const MAX_LOG_VALUE_LENGTH = 20000
const CONSOLE_METHODS = ['log', 'info', 'warn', 'error', 'debug'] as const

type VConsoleInstance = {
  destroy: () => void
}

type ConsoleMethod = (typeof CONSOLE_METHODS)[number]

type AllLogEntry = {
  level: ConsoleMethod
  text: string
  timestamp: Date
}

let vConsoleInstance: VConsoleInstance | undefined
let vConsoleLoadingPromise: Promise<boolean> | undefined
let allLogsWindow: HTMLElement | undefined
let allLogsContainer: HTMLElement | undefined
let allLogEntries: AllLogEntry[] = []
let originalConsoleMethods:
  | Partial<Record<ConsoleMethod, (...args: any[]) => void>>
  | undefined

function getStoredDebugConsoleEnabled() {
  try {
    return localStorage.getItem(DEBUG_CONSOLE_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

function setStoredDebugConsoleEnabled(enabled: boolean) {
  try {
    if (enabled) {
      localStorage.setItem(DEBUG_CONSOLE_STORAGE_KEY, 'true')
      return
    }

    localStorage.removeItem(DEBUG_CONSOLE_STORAGE_KEY)
  } catch {
    // Debug mode can still work for the current page when storage is unavailable.
  }
}

export function shouldEnableDebugConsole() {
  return getStoredDebugConsoleEnabled()
}

function stringifyLogValue(value: any) {
  if (value instanceof Error) {
    return value.stack || `${value.name}: ${value.message}`
  }

  if (typeof value === 'string') return value

  try {
    const seen = new WeakSet<object>()
    const serializedValue = JSON.stringify(
      value,
      (_key, nestedValue) => {
        if (typeof nestedValue === 'bigint') return String(nestedValue)
        if (typeof nestedValue !== 'object' || nestedValue === null) {
          return nestedValue
        }
        if (seen.has(nestedValue)) return '[Circular]'

        seen.add(nestedValue)
        return nestedValue
      },
      2
    )

    return serializedValue ?? String(value)
  } catch {
    return String(value)
  }
}

function getAllLogsText() {
  return allLogEntries
    .map((entry) => {
      return `[${entry.timestamp.toISOString()}] [${entry.level.toUpperCase()}] ${
        entry.text
      }`
    })
    .join('\n')
}

function renderAllLogs() {
  if (!allLogsContainer) return

  allLogsContainer.textContent = getAllLogsText() || 'No logs captured.'
  allLogsContainer.scrollTop = allLogsContainer.scrollHeight
}

function createWindowButton(label: string, onClick: () => void) {
  const button = document.createElement('button')
  button.type = 'button'
  button.textContent = label
  button.style.cssText = [
    'border:1px solid #666',
    'border-radius:4px',
    'padding:2px 7px',
    'color:#fff',
    'background:#444',
    'font:12px sans-serif',
    'cursor:pointer',
  ].join(';')
  button.addEventListener('click', onClick)

  return button
}

function createAllLogsWindow() {
  if (allLogsWindow) return

  allLogsWindow = document.createElement('section')
  allLogsWindow.setAttribute('aria-label', 'All debug logs')
  allLogsWindow.style.cssText = [
    'position:fixed',
    'left:12px',
    'bottom:12px',
    'z-index:2147483646',
    'display:flex',
    'flex-direction:column',
    'width:min(560px,calc(100vw - 24px))',
    'height:min(420px,calc(100vh - 24px))',
    'min-width:280px',
    'min-height:160px',
    'overflow:hidden',
    'resize:both',
    'border:1px solid #666',
    'border-radius:8px',
    'box-shadow:0 8px 30px rgba(0,0,0,.45)',
    'background:#242424',
  ].join(';')

  const header = document.createElement('header')
  header.style.cssText = [
    'display:flex',
    'align-items:center',
    'gap:6px',
    'padding:7px 8px',
    'color:#fff',
    'background:#333',
    'font:bold 13px sans-serif',
    'cursor:move',
    'user-select:none',
  ].join(';')

  const title = document.createElement('span')
  title.textContent = 'All Logs'
  title.style.marginRight = 'auto'

  allLogsContainer = document.createElement('pre')
  allLogsContainer.style.cssText = [
    'box-sizing:border-box',
    'flex:1',
    'margin:0',
    'overflow:auto',
    'padding:10px',
    'white-space:pre-wrap',
    'word-break:break-word',
    'font:12px/1.5 monospace',
    'color:#d8d8d8',
    'background:#242424',
  ].join(';')

  const copyButton = createWindowButton('Copy', () => {
    void navigator.clipboard?.writeText(getAllLogsText())
  })
  const clearButton = createWindowButton('Clear', () => {
    allLogEntries = []
    renderAllLogs()
  })
  let isMinimized = false
  const setMinimized = (minimized: boolean) => {
    isMinimized = minimized
    title.style.display = isMinimized ? 'none' : 'inline'
    clearButton.style.display = isMinimized ? 'none' : 'inline-block'
    copyButton.style.display = isMinimized ? 'none' : 'inline-block'
    allLogsContainer!.style.display = isMinimized ? 'none' : 'block'
    allLogsWindow!.style.width = isMinimized
      ? 'auto'
      : 'min(560px,calc(100vw - 24px))'
    allLogsWindow!.style.height = isMinimized
      ? 'auto'
      : 'min(420px,calc(100vh - 24px))'
    allLogsWindow!.style.minWidth = isMinimized ? '0' : '280px'
    allLogsWindow!.style.minHeight = isMinimized ? '0' : '160px'
    allLogsWindow!.style.resize = isMinimized ? 'none' : 'both'
    header.style.padding = isMinimized ? '0' : '7px 8px'
    header.style.cursor = isMinimized ? 'default' : 'move'
    minimizeButton.textContent = isMinimized ? 'Logs' : 'Minimize'
  }
  const minimizeButton = createWindowButton('Logs', () => {
    setMinimized(!isMinimized)
  })

  let dragOffsetX = 0
  let dragOffsetY = 0
  const onPointerMove = (event: PointerEvent) => {
    if (!allLogsWindow) return

    allLogsWindow.style.left = `${Math.max(0, event.clientX - dragOffsetX)}px`
    allLogsWindow.style.top = `${Math.max(0, event.clientY - dragOffsetY)}px`
    allLogsWindow.style.right = 'auto'
    allLogsWindow.style.bottom = 'auto'
  }
  const stopDragging = () => {
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', stopDragging)
  }

  header.addEventListener('pointerdown', (event) => {
    if ((event.target as HTMLElement).tagName === 'BUTTON') return

    const bounds = allLogsWindow!.getBoundingClientRect()
    dragOffsetX = event.clientX - bounds.left
    dragOffsetY = event.clientY - bounds.top
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', stopDragging)
  })

  header.append(title, clearButton, copyButton, minimizeButton)
  allLogsWindow.append(header, allLogsContainer)
  document.body.append(allLogsWindow)
  setMinimized(true)
  renderAllLogs()
}

function destroyAllLogsWindow() {
  allLogsWindow?.remove()
  allLogsWindow = undefined
  allLogsContainer = undefined
}

function addAllLogEntry(level: ConsoleMethod, values: any[]) {
  const text = values
    .map(stringifyLogValue)
    .join(' ')
    .slice(0, MAX_LOG_VALUE_LENGTH)

  allLogEntries.push({
    level,
    text,
    timestamp: new Date(),
  })

  if (allLogEntries.length > MAX_ALL_LOG_ENTRIES) {
    allLogEntries = allLogEntries.slice(-MAX_ALL_LOG_ENTRIES)
  }

  renderAllLogs()
}

function installAllLogsConsoleCapture() {
  if (originalConsoleMethods) return

  originalConsoleMethods = {}

  CONSOLE_METHODS.forEach((method) => {
    const originalMethod = console[method].bind(console)
    originalConsoleMethods![method] = originalMethod
    console[method] = (...args: any[]) => {
      addAllLogEntry(method, args)
      originalMethod(...args)
    }
  })
}

function uninstallAllLogsConsoleCapture() {
  if (!originalConsoleMethods) return

  CONSOLE_METHODS.forEach((method) => {
    const originalMethod = originalConsoleMethods?.[method]
    if (originalMethod) {
      console[method] = originalMethod
    }
  })

  originalConsoleMethods = undefined
  allLogEntries = []
  destroyAllLogsWindow()
}

function enableAllLogsWindow() {
  createAllLogsWindow()
  installAllLogsConsoleCapture()
}

export async function enableDebugConsole(persist = true) {
  if (vConsoleInstance) return true
  if (vConsoleLoadingPromise) return vConsoleLoadingPromise

  vConsoleLoadingPromise = import('vconsole')
    .then(({ default: VConsole }) => {
      vConsoleInstance = VConsole.instance ?? new VConsole()
      enableAllLogsWindow()

      if (persist) {
        setStoredDebugConsoleEnabled(true)
      }

      return true
    })
    .finally(() => {
      vConsoleLoadingPromise = undefined
    })

  return vConsoleLoadingPromise
}

export function disableDebugConsole() {
  uninstallAllLogsConsoleCapture()
  vConsoleInstance?.destroy()
  vConsoleInstance = undefined
  setStoredDebugConsoleEnabled(false)

  return false
}

export async function toggleDebugConsole() {
  if (vConsoleInstance) return disableDebugConsole()

  return enableDebugConsole()
}

export function initializeDebugConsole() {
  if (shouldEnableDebugConsole()) {
    void enableDebugConsole(false).catch(() => undefined)
  }
}
